import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../store';
import { Toolbar, IconButton, Tooltip, Typography, Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import BSmart_color from '../../../resources/icons/BSmart.png';
import Mammography_icon from '../../../resources/icons/mammography_dark.png';
import { Autorenew } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import { useFilterOptionLabels } from '../FilterDrawer/FilterDrawer.utils';
import ExamListDateFilterControls from '../DrawerExamList/DatePicker/ExamListDateFilterControls';

const useStyles = makeStyles((theme) => ({
  grow_toolbar: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '100%',
    backgroundColor: theme.palette.background.mainDark,
    transition: 'padding-left 0.8s ease-in-out',
    maxWidth: '100%',
    paddingRight: '10px',
    gap: '10px',
  },

  toolbar_applied_filters: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    gap: '6px',
    flexGrow: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    scrollbarColor: 'rgb(68, 68, 68) rgb(143, 143, 143)',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      height: 4,
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 6,
    },
  },

  reset_nav_container: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
  },

  navButton: {
    background: theme.palette.accents.main,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px !important',
    boxShadow:
      '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
    '&:hover': {
      background: theme.palette.accents.dark,
      boxShadow: '0px 0px 5px 0px rgb(180 180 180 / 78%)',
    },
  },

  nav_tooltip_text: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: '12px',
  },

  tooltip_text: {
    letterSpacing: '0.1rem',
    textTransform: 'capitalize',
    fontSize: '12px',
  },

  general_chip: {
    marginLeft: '10px',
    fontSize: theme.fonts.responsiveMediumBig,
    '& .MuiChip-deleteIcon': {
      fontSize: theme.fonts.responsiveMediumBig,
      marginLeft: '5px',
      '&:hover': {
        color: 'primary',
      },
    },
  },

  search_chip: {
    border: '1px solid #dedede',
    color: '#dedede',
  },

  custom_filter_chip: {
    border: `1px solid ${theme.palette.accents.main}`,
    color: theme.palette.accents.main,
  },
}));

export default function NavBar(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { filterOptionsLabels } = useFilterOptionLabels();
  const { examinationListDrawer, landscape, initialFilters } = useSelector((state) => state.gui);
  const dates = useSelector((state) => state.dates);
  const filters = useSelector((state) => state.filter);
  const { operator_list, device_list, institution_list } = useSelector((state) => state.operators_devices);
  const history = useHistory();
  const appliedFiltersRef = useRef(null);

  const handleAppliedFiltersWheel = useCallback((event) => {
    const container = appliedFiltersRef.current;
    if (!container) return;

    const canScrollX = container.scrollWidth > container.clientWidth;
    const canScrollY = container.scrollHeight > container.clientHeight;

    if (!canScrollX && !canScrollY) return;

    if (canScrollX) {
      const delta = event.deltaY !== 0 ? event.deltaY : event.deltaX;
      if (!delta) return;
      event.preventDefault();
      container.scrollLeft += delta;
      return;
    }

    if (canScrollY && event.deltaY !== 0) {
      event.preventDefault();
      container.scrollTop += event.deltaY;
    }
  }, []);

  useEffect(() => {
    const container = appliedFiltersRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleAppliedFiltersWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleAppliedFiltersWheel);
    };
  }, [handleAppliedFiltersWheel]);

  const availableChipOptions = useMemo(() => {
    const drawerOptions = Object.entries(initialFilters || {}).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const operatorAliases = (operator_list || [])
      .map((op) => op.aliases || [])
      .flat()
      .filter(Boolean);
    const uniqueOperators = Array.from(new Set(operatorAliases));
    const uniqueDevices = Array.from(new Set((device_list || []).map((dev) => dev.device).filter(Boolean)));
    const uniqueInstitutions = Array.from(
      new Set((institution_list || []).map((inst) => inst.institution).filter(Boolean)),
    );

    return {
      ...drawerOptions,
      operators: uniqueOperators,
      devices: uniqueDevices,
      institutions: uniqueInstitutions,
    };
  }, [initialFilters, operator_list, device_list, institution_list]);

  const chipsToRender = useMemo(() => {
    const entries = Object.entries(filters || {});
    const result = [];
    const translateValue = (rawValue) => {
      const fallback = String(rawValue);
      const translated = t(`Filters.${fallback}`);
      return translated === `Filters.${fallback}` ? fallback : translated;
    };

    for (const [key, value] of entries) {
      if (key === 'dates' || key === 'search' || key === 'study_list' || key === 'study_name') continue;
      if (value === undefined || value === null) continue;

      if (key === 'multiExams') {
        if (value !== true) continue;
        result.push({
          key,
          label: t('Filters.multi_exams'),
          tooltip: t('Filters.multi_exams'),
        });
        continue;
      }

      if (key === 'multiCaptures') {
        if (value !== true) continue;
        result.push({
          key,
          label: t('Filters.multi_captures'),
          tooltip: t('Filters.multi_captures'),
        });
        continue;
      }

      if (key === 'featuresQuality' && typeof value === 'object' && !Array.isArray(value)) {
        const title = value.label || t('Filters.featuresQuality');
        const projection = value.projection ? String(value.projection) : '';
        const translatedValue = value.value ? translateValue(value.value) : '';
        const details = [projection, translatedValue].filter(Boolean).join(' & ');

        result.push({
          key,
          label: details ? `${title}: ${details}` : title,
          tooltip: details ? `${title}: ${details}` : title,
        });
        continue;
      }

      if (typeof value === 'boolean') {
        if (value !== true) continue;
        result.push({
          key,
          label: t(`Filters.${key}`),
          tooltip: t(`Filters.${key}`),
        });
        continue;
      }

      if (Array.isArray(value)) {
        const cleaned = value.filter((v) => v !== '' && v !== undefined && v !== null);
        if (!cleaned.length) continue;

        const filterTitle = t(`Filters.${key}`);

        if (key in availableChipOptions && Array.isArray(availableChipOptions[key])) {
          const allOptions = availableChipOptions[key] || [];
          const allLabels = filterOptionsLabels(key, allOptions);
          const labelMap = new Map(allOptions.map((opt, idx) => [opt, allLabels[idx] ?? String(opt)]));
          const selectedLabels = cleaned.map((v) => labelMap.get(v) ?? String(v));

          result.push({
            key,
            label: `${filterTitle}: ${selectedLabels.join(', ')}`,
            tooltip: `${filterTitle}: ${selectedLabels.join(', ')}`,
          });
        } else {
          const selectedLabels = cleaned.map(translateValue);
          if (key === 'screening') {
            selectedLabels[0] =
              selectedLabels[0] === 'No' || selectedLabels[0] === 'no'
                ? t('Filters.screening_diagnostic')
                : t('Filters.screening_screening');
          }
          result.push({
            key,
            label: `${filterTitle}: ${selectedLabels.join(', ')}`,
            tooltip: `${filterTitle}: ${selectedLabels.join(', ')}`,
          });
        }
      }
    }

    return result;
  }, [filters, availableChipOptions, filterOptionsLabels, t]);

  const resetSingleFilter = (filterKey) => {
    dispatch(actions.setFilterProperty([filterKey, null]));
  };

  useEffect(() => {
    dispatch(actions.setCompressionDates(null));
    dispatch(actions.setPerformanceDates(null));
  }, [dates.customDate, dates.selectedRange, dispatch]);

  const isDashboard = window.location.pathname.includes('dashboard');

  return (
    !window.location.pathname.includes('ifu') && (
      <Toolbar className={classes.grow_toolbar} style={{ paddingLeft: props.leftAlignment }}>
        {!examinationListDrawer && (
          <ExamListDateFilterControls filterIsActive={props.activeFilter} variant='navbar' />
        )}

        <div ref={appliedFiltersRef} className={classes.toolbar_applied_filters}>
          {filters.search && (
            <Tooltip
              arrow
              placement='bottom-end'
              title={
                <Typography color='inherit' className={classes.tooltip_text}>
                  {t('Menu.searched_patients', { search: filters.search })}
                </Typography>
              }>
              <Chip
                icon={<SearchIcon fontSize='small' style={{ height: '12px', width: '12px' }} />}
                label={filters.search}
                variant='outlined'
                size='small'
                className={`${classes.general_chip} ${classes.search_chip}`}
                onDelete={() => dispatch(actions.setFilterProperty(['search', '']))}
              />
            </Tooltip>
          )}
          {filters.study_name && (
            <Tooltip
              arrow
              placement='bottom-end'
              title={
                <Typography color='inherit' className={classes.tooltip_text}>
                  {`${t('Filters.custom_filter')}: ${filters.study_name}`}
                </Typography>
              }>
              <Chip
                label={`${t('Filters.custom_filter')}: ${filters.study_name}`}
                variant='outlined'
                size='small'
                className={`${classes.general_chip} ${classes.custom_filter_chip}`}
                onDelete={() => {
                  dispatch(actions.setFilterProperty(['study_name', null]));
                  dispatch(actions.setFilterProperty(['study_list', null]));
                }}
              />
            </Tooltip>
          )}
          {chipsToRender.map(({ key, label, tooltip }) => (
            <Tooltip
              key={key}
              arrow
              placement='bottom-end'
              title={
                <Typography color='inherit' className={classes.tooltip_text}>
                  {tooltip}
                </Typography>
              }>
              <Chip
                label={label}
                color='secondary'
                variant='outlined'
                size='small'
                onDelete={() => resetSingleFilter(key)}
                style={{ textTransform: 'capitalize', maxWidth: '300px' }}
              />
            </Tooltip>
          ))}
        </div>

        {props.activeFilter && (
          <Tooltip
            arrow
            placement='bottom-end'
            title={
              <Typography color='inherit' className={classes.tooltip_text}>
                {t('Menu.reset_filter')}
              </Typography>
            }>
            <Chip
              icon={<Autorenew />}
              className={classes.general_chip}
              label={t('Menu.reset_all_filters')}
              color='secondary'
              variant='default'
              size='small'
              onClick={() => dispatch(actions.resetFilter())}
            />
          </Tooltip>
        )}

        <div className={classes.reset_nav_container}>
          <Tooltip
            arrow
            placement='bottom-end'
            title={
              <Typography className={classes.nav_tooltip_text} color='inherit'>
                {isDashboard ? t('Menu.view_patient') : t('Menu.view_dashboard')}
              </Typography>
            }>
            <IconButton
              aria-label='nav-navigation'
              className={classes.navButton}
              size='small'
              id='nav-cta-navigation'
              onClick={() => {
                if (isDashboard) {
                  history.push('/bbox/results/details');
                  if (!examinationListDrawer) dispatch(actions.setExaminationListDrawer(true));
                } else {
                  history.push('/bbox/results/dashboard');
                  dispatch(actions.setExaminationListDrawer(false));
                }
              }}
              style={{
                width: landscape ? '3vh' : 'auto',
                height: landscape ? '3vh' : '100%',
              }}>
              <img
                src={isDashboard ? Mammography_icon : BSmart_color}
                alt={isDashboard ? 'Examination icon' : 'b-SMART icon'}
                style={{ height: '1em', maxHeight: !landscape ? '12px' : undefined }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </Toolbar>
    )
  );
}
